'use client'

/**
 * <PrivacyReConsentModal /> — R103.56 (2026-06-05 abend)
 *
 * Stephan-Anweisung: „aktive Bestaetigung mit Hinweis dass sich die
 * Datenschutzvereinbarung geaendert hat, mit Bitte um Bestaetigung —
 * geht auch als E-Mail raus, Grund muss nicht angegeben werden, ist nur
 * eine kleine Aenderung".
 *
 * Globale Komponente im Root-Layout. Polled bei jeder Session-Aenderung
 * `/api/user/privacy-consent` (GET). Zeigt blocking-Modal bis User
 * „Zustimmen" oder „Spaeter" klickt. „Zustimmen" → POST + Modal weg.
 * „Spaeter" → Modal zu (nach 1 h Snooze, sonst kommt es bei jedem
 * Page-Mount wieder).
 *
 * Der User wird waehrenddessen aus oeffentlichen Wertungs-APIs gefiltert
 * — bis er zustimmt. Aktive Einwilligung im Sinne von Art. 7 DSGVO.
 */

import { useEffect, useState } from 'react'
import { useSession } from 'next-auth/react'
import Link from 'next/link'
import { ShieldCheck, ExternalLink } from 'lucide-react'

// R104.2 (Stephan-Spec 2026-06-05 abend): "Modal erscheint bei JEDEM
// Login bis User Ja sagt". Wir nutzen sessionStorage statt localStorage
// → ueberlebt KEIN Browser-Neustart. Plus minimaler 60s-Snooze, damit
// das Modal nicht innerhalb desselben Page-Wechsels mehrfach hochpoppt.
const SNOOZE_KEY = 'fgl_privacy_reconsent_snoozed_until'
const SNOOZE_MS = 60 * 1000 // 60 s — verhindert Doppel-Trigger

export function PrivacyReConsentModal() {
  const { data: session, status } = useSession()
  const [needs, setNeeds] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (status !== 'authenticated' || !session?.user?.id) {
      setNeeds(false)
      return
    }
    // Snooze pruefen (sessionStorage → ueberlebt KEIN Browser-Restart)
    try {
      const until = parseInt(sessionStorage.getItem(SNOOZE_KEY) || '0', 10)
      if (until > Date.now()) return
    } catch { /* ignore */ }

    let cancelled = false
    fetch('/api/user/privacy-consent')
      .then(r => r.ok ? r.json() : { needsReConsent: false })
      .then(j => { if (!cancelled && j?.needsReConsent) setNeeds(true) })
      .catch(() => { /* schlucken — nicht-blocking */ })
    return () => { cancelled = true }
  }, [status, session?.user?.id])

  const grant = async () => {
    setSubmitting(true)
    try {
      const res = await fetch('/api/user/privacy-consent', { method: 'POST' })
      if (res.ok) {
        try { sessionStorage.removeItem(SNOOZE_KEY) } catch {}
        setNeeds(false)
      }
    } finally {
      setSubmitting(false)
    }
  }

  const snooze = () => {
    try { sessionStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS)) } catch {}
    setNeeds(false)
  }

  if (!needs) return null

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{
        background: 'color-mix(in oklch, var(--bg) 70%, transparent)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="privacy-reconsent-title"
    >
      <div
        className="w-full max-w-lg rounded-2xl p-6 sm:p-8"
        style={{
          background: 'var(--bg-card)',
          border: '1.5px solid color-mix(in oklch, var(--accent) 38%, transparent)',
          boxShadow: '0 24px 56px -16px rgba(0,0,0,0.55), 0 0 32px -8px color-mix(in oklch, var(--accent) 28%, transparent)',
        }}
      >
        <div
          className="mx-auto mb-4 inline-flex items-center justify-center rounded-full"
          style={{
            width: 64,
            height: 64,
            background: 'color-mix(in oklch, var(--accent) 14%, transparent)',
            border: '1.5px solid color-mix(in oklch, var(--accent) 45%, transparent)',
          }}
        >
          <ShieldCheck className="h-7 w-7" style={{ color: 'var(--accent)' }} />
        </div>

        <h2
          id="privacy-reconsent-title"
          className="text-[20px] font-black mb-2 text-center"
          style={{ color: 'var(--text)' }}
        >
          Unsere Datenschutzerklärung wurde aktualisiert
        </h2>

        <p
          className="text-[14px] leading-relaxed mb-5 text-center"
          style={{ color: 'var(--text-muted, var(--text))' }}
        >
          Bitte bestätige die kleine Änderung, damit Deine Wertungsteilnahme
          ohne Unterbrechung weiterläuft.
        </p>

        <div className="text-center mb-6">
          <Link
            href="/datenschutz"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold underline"
            style={{ color: 'var(--accent)' }}
          >
            Datenschutzerklärung lesen
            <ExternalLink className="h-3.5 w-3.5" />
          </Link>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={grant}
            disabled={submitting}
            className="flex-1 rounded-full px-5 py-3 font-bold text-[14px] transition-all hover:opacity-90 disabled:opacity-50"
            style={{
              background: 'var(--accent)',
              color: '#1a1306',
            }}
          >
            {submitting ? 'Wird gespeichert…' : 'Zustimmen'}
          </button>
          <button
            onClick={snooze}
            disabled={submitting}
            className="flex-1 rounded-full px-5 py-3 font-bold text-[14px] transition-all hover:opacity-80"
            style={{
              background: 'transparent',
              color: 'var(--text-muted, var(--text))',
              border: '1.5px solid color-mix(in oklch, var(--text) 18%, transparent)',
            }}
          >
            Später
          </button>
        </div>

        <p
          className="text-[11px] mt-5 text-center"
          style={{ color: 'var(--text-muted, var(--text))', opacity: 0.7 }}
        >
          Solange Du nicht zustimmst, wirst Du nicht in der öffentlichen
          Wertung angezeigt. Du kannst jederzeit in den Einstellungen widerrufen.
        </p>
      </div>
    </div>
  )
}
